'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    deleteSupplierType,
    getSupplierTypesByOrganization,
    SupplierType,
} from '@/lib/api/suppliers/supplier-type-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdCategory,
    MdDelete,
    MdEdit,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function SupplierTypesListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('registrations', 'supplier_types')
    const [types, setTypes] = useState<SupplierType[]>([])
    const [loading, setLoading] = useState(true)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (user?.organizationId) loadTypes()
    }, [user?.organizationId])

    const loadTypes = async () => {
        if (!user?.organizationId) return
        setLoading(true)
        try {
            const response = await getSupplierTypesByOrganization(user.organizationId)
            if (response.success) {
                setTypes(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar tipos')
            }
        } catch {
            toast.error('Erro ao carregar tipos')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setItemToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!itemToDelete) return
        setDeleting(true)
        try {
            const response = await deleteSupplierType(itemToDelete)
            if (response.success) {
                toast.success('Tipo excluído com sucesso')
                loadTypes()
                setShowDeleteModal(false)
                setItemToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir tipo')
            }
        } catch {
            toast.error('Erro ao excluir tipo')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando tipos de fornecedor...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdCategory className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Tipos de Fornecedor
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {types.length} {types.length === 1 ? 'tipo' : 'tipos'} cadastrados
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/registrations/supplier-types/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Adicionar</span>
                    </button>
                )}
            </div>

            {types.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdCategory className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhum tipo cadastrado
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Crie tipos para categorizar seus fornecedores
                        </p>
                        {canCreate && (
                            <button
                                onClick={() => navigate('/registrations/supplier-types/new')}
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro tipo
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {types.map(type => (
                        <div
                            key={type.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                        >
                            <div className="flex items-center gap-4 p-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-base font-semibold text-base-content">
                                            {type.name}
                                        </h3>
                                    </div>
                                    <p className="text-sm text-base-content/60">
                                        {type._count?.suppliers || 0}{' '}
                                        {(type._count?.suppliers || 0) === 1 ? 'fornecedor' : 'fornecedores'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1">
                                    {canUpdate && (
                                        <button
                                            onClick={() => navigate(`/registrations/supplier-types/${type.id}`)}
                                            className="btn btn-sm btn-ghost"
                                            title="Editar"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            onClick={() => handleDelete(type.id)}
                                            className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                            title="Excluir"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setItemToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir tipo de fornecedor"
                message="Tem certeza de que deseja excluir este tipo? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
